import React from "react";
import { Phone, MessageCircle, Clock, Truck, Sparkles, MapPin, Check, ArrowRight, Mail } from "lucide-react";
import { LivraisonHeroMedia } from "../components/LivraisonHeroMedia";
import { useSEO } from "../lib/useSEO";
import { OrganizationSchema } from "../components/SEOSchema";
import { FAQSection } from "../components/FAQSection";
import { ServiceTerms } from "../components/ServiceTerms";
import { CITY_LIST } from "../data/cities";
import { CATEGORY_IMAGES } from "../lib/siteImages";

const CARD_IMAGES = {
  pizza:
    `${process.env.REACT_APP_BACKEND_URL}/api/static/products/pizza-burrata.png`,
  softs: CATEGORY_IMAGES.soft_drinks,
  snacks: CATEGORY_IMAGES.snacks,
  planches: CATEGORY_IMAGES.planches,
};

const PHONE = "06 23 06 03 08";
const PHONE_INTL = "+33623060308";
const WHATSAPP = "https://wa.me/33623060308";

const trackLeadClick = (source) => {
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("event", "conversion", {
      send_to: "AW-18374793949",
      event_category: "lead",
      event_label: source,
    });
  }
};

const Livraison = () => {
  useSEO({
    title: "NOCTUA RIVIERA — Livraison de nuit 24h/24 Côte d'Azur (Cannes à Monaco)",
    description: "Livraison de Cannes à Monaco : boissons et snacks 24h/24, restauration de 11h à 4h. Minimum 30 €, livraison fixe 15 €. Espèces, carte ou lien sécurisé.",
    path: "/livraison",
  });

  return (
    <div className="min-h-screen bg-[#0a0f1a] text-white" data-testid="livraison-page">
      <OrganizationSchema />
      {/* MINIMAL HEADER */}
      <header className="fixed top-0 inset-x-0 z-40 bg-[#0a0f1a]/90 backdrop-blur-md border-b border-white/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-display text-xl sm:text-2xl tracking-tight" data-testid="livraison-brand">
              NOCTUA <span className="text-[#d4af37] italic">Riviera</span>
            </span>
          </div>
          <div className="flex items-center gap-3 sm:gap-5">
            <a
              href="/"
              rel="nofollow noopener"
              className="hidden sm:inline-flex items-center gap-2 px-4 py-2 border border-[#d4af37]/60 text-[#d4af37] hover:bg-[#d4af37] hover:text-[#0a0f1a] transition-colors text-xs uppercase tracking-[0.2em]"
              data-testid="header-full-menu-link"
            >
              Menu complet
            </a>
            <a
              href="/"
              rel="nofollow noopener"
              className="sm:hidden text-[#d4af37] text-xs uppercase tracking-[0.15em] underline underline-offset-4 decoration-[#d4af37]/40"
              data-testid="header-full-menu-link-mobile"
            >
              Menu
            </a>
            <a
              href={`tel:${PHONE_INTL}`}
              onClick={() => trackLeadClick("header_phone")}
              className="inline-flex items-center gap-2 text-[#d4af37] hover:text-white transition-colors text-sm"
              aria-label={`Appeler le ${PHONE}`}
              data-testid="header-phone-link"
            >
              <Phone size={14} />
              <span className="hidden sm:inline">{PHONE}</span>
            </a>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="livraison-hero relative flex items-end overflow-hidden pt-16" data-testid="livraison-hero">
        <LivraisonHeroMedia />

        <div className="livraison-hero-content relative max-w-7xl mx-auto w-full px-5 sm:px-8 pb-16 sm:pb-24 pt-24 z-10">
          <p className="text-xs uppercase tracking-[0.3em] text-[#d4af37] mb-5" data-testid="livraison-hero-location">
            Côte d'Azur · 24h/24
          </p>
          <h1 className="livraison-hero-title font-display text-4xl sm:text-5xl lg:text-6xl leading-none max-w-4xl" data-testid="livraison-hero-title">
            Livraison de nuit,
            <br />
            <span className="italic text-[#d4af37]">Cannes</span> à{" "}
            <span className="italic text-[#d4af37]">Monaco</span>.
          </h1>
          <div className="h-[1px] w-24 bg-[#d4af37] my-8" />
          <p className="text-base sm:text-lg text-gray-300 max-w-xl leading-relaxed" data-testid="livraison-hero-description">
            Snacks, plateaux et boissons livrés 24h/24 de Cannes à Monaco.
            Pizzas, pâtes et desserts disponibles de 11h à 4h du matin, 7 jours sur 7.
          </p>

          <div className="livraison-hero-actions flex flex-col sm:flex-row gap-3 mt-10">
            <a
              href={`tel:${PHONE_INTL}`}
              onClick={() => trackLeadClick("hero_call")}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#d4af37] text-[#0a0f1a] font-medium hover:bg-white transition-colors uppercase tracking-[0.2em] text-xs"
              data-testid="hero-call-btn"
            >
              <Phone size={14} />
              Commander : {PHONE}
            </a>
            <a
              href={WHATSAPP}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackLeadClick("hero_whatsapp")}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-[#d4af37] text-[#d4af37] hover:bg-[#d4af37] hover:text-[#0a0f1a] transition-colors uppercase tracking-[0.2em] text-xs"
              data-testid="hero-whatsapp-btn"
            >
              <MessageCircle size={14} />
              WhatsApp
            </a>
          </div>

          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-12 text-xs uppercase tracking-[0.2em] text-gray-400">
            <span className="flex items-center gap-2" data-testid="livraison-hero-hours">
              <Clock size={12} className="text-[#d4af37]" /> Ouvert 24/24
            </span>
            <span className="flex items-center gap-2" data-testid="livraison-hero-delivery-time">
              <Truck size={12} className="text-[#d4af37]" /> Livraison ~30 min
            </span>
            <span className="flex items-center gap-2" data-testid="livraison-hero-availability">
              <Sparkles size={12} className="text-[#d4af37]" /> 7 jours sur 7
            </span>
          </div>
        </div>
      </section>

      {/* CATEGORIES SHOWCASE */}
      <section id="carte" className="py-24 sm:py-32 bg-[#0a0f1a] scroll-mt-16">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl mb-16">
            <p className="text-xs uppercase tracking-[0.3em] text-[#d4af37] mb-3">
              Notre carte
            </p>
            <h2 className="font-display text-4xl sm:text-5xl leading-none">
              Ce que vous pouvez <span className="italic text-[#d4af37]">commander</span>
            </h2>
            <ServiceTerms testId="livraison-service-terms" className="mt-6" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[
              {
                id: "restauration",
                img: CARD_IMAGES.pizza,
                eyebrow: "Restauration",
                title: "Pizzas, pâtes, desserts",
                desc:
                  "Pizzas, pâtes et desserts — restauration disponible de 11h à 4h du matin.",
              },
              {
                id: "plateaux",
                img: CARD_IMAGES.planches,
                eyebrow: "Plateaux",
                title: "Charcuterie & fromages",
                desc:
                  "Planches raffinées de 2 à 4 personnes. Parfait pour vos soirées entre amis.",
              },
              {
                id: "boissons",
                img: CARD_IMAGES.softs,
                eyebrow: "Boissons",
                title: "Sodas, jus, eaux",
                desc:
                  "Coca, Schweppes, Fanta, Oasis, Perrier, Evian… tout ce qu'il faut, au frais.",
              },
              {
                id: "snacks",
                img: CARD_IMAGES.snacks,
                eyebrow: "Snacks",
                title: "Chips, bonbons, biscuits",
                desc:
                  "Pringles, Doritos, Haribo, Oreo, Kinder… pour caler toutes les envies.",
              },
            ].map((c, i) => (
              <div
                key={c.id}
                id={c.id}
                className="group relative aspect-[4/3] overflow-hidden border border-white/10 hover:border-[#d4af37]/50 transition-all scroll-mt-20"
                data-testid={`category-card-${i}`}
              >
                <img
                  src={c.img}
                  alt={c.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0f1a] via-[#0a0f1a]/60 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8">
                  <p className="text-xs uppercase tracking-[0.3em] text-[#d4af37] mb-2">
                    {c.eyebrow}
                  </p>
                  <h3 className="font-display text-2xl sm:text-3xl leading-none">
                    {c.title}
                  </h3>
                  <p className="text-sm text-gray-300 mt-3 leading-relaxed max-w-sm">
                    {c.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <a
              href={`tel:${PHONE_INTL}`}
              onClick={() => trackLeadClick("catalog_call")}
              className="inline-flex items-center gap-2 text-[#d4af37] hover:text-white text-sm uppercase tracking-[0.2em] transition-colors"
              data-testid="catalog-call-link"
            >
              Voir la carte complète : appelez-nous <ArrowRight size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 sm:py-32 bg-[#0e1524]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p className="text-xs uppercase tracking-[0.3em] text-[#d4af37] mb-3">
              Comment ça marche
            </p>
            <h2 className="font-display text-4xl sm:text-5xl leading-none">
              En trois <span className="italic text-[#d4af37]">étapes</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {[
              {
                n: "01",
                t: "Vous nous appelez",
                d: "Un simple appel ou message WhatsApp au " + PHONE + ". On note votre commande.",
              },
              {
                n: "02",
                t: "On confirme",
                d: "On vous confirme la disponibilité, le prix et le délai. Paiement à la livraison ou par lien sécurisé.",
              },
              {
                n: "03",
                t: "Livraison à domicile",
                d: "30 minutes en moyenne. On sonne, on vous remet la commande, bonne soirée.",
              },
            ].map((s) => (
              <div key={s.n} className="border-l-2 border-[#d4af37] pl-6">
                <p className="font-display text-6xl text-[#d4af37] leading-none">
                  {s.n}
                </p>
                <h3 className="font-display text-2xl mt-4">{s.t}</h3>
                <p className="text-sm text-gray-400 mt-3 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="py-24 sm:py-32 bg-[#0a0f1a]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#d4af37] mb-3">
                Pourquoi Noctua
              </p>
              <h2 className="font-display text-4xl sm:text-5xl leading-none mb-8">
                La <span className="italic text-[#d4af37]">Riviera</span>, servie
                à votre porte.
              </h2>
              <p className="text-gray-300 leading-relaxed mb-8">
                Quand tout est fermé, nous sommes là. Une équipe locale, des produits
                frais, une livraison rapide — pour vos soirées, vos hôtels, vos yachts,
                vos moments imprévus.
              </p>
              <ul className="space-y-4">
                {[
                  "Service 24h/24, y compris jours fériés",
                  "Livraison rapide : 20 à 40 minutes selon la zone",
                  "Paiement CB, espèces ou lien sécurisé",
                  "Équipe francophone et anglophone",
                  "Zone couverte : Cannes, Antibes, Nice, Monaco et alentours",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-3">
                    <Check
                      size={18}
                      className="text-[#d4af37] flex-shrink-0 mt-0.5"
                    />
                    <span className="text-gray-200">{f}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="relative aspect-[4/5] overflow-hidden border border-white/10">
              <img
                src={CARD_IMAGES.pizza}
                alt="Pizza artisanale livrée à domicile Côte d'Azur"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0f1a]/80 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <p className="text-xs uppercase tracking-[0.3em] text-[#d4af37] mb-2">
                  Signature
                </p>
                <p className="font-display text-3xl">Pizza Burrata</p>
                <p className="text-sm text-gray-300 mt-2">
                  Cuisson feu de bois, burrata fraîche des Pouilles.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DELIVERY ZONE */}
      <section id="zone" className="py-24 sm:py-32 bg-[#0e1524] scroll-mt-16">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 text-center">
          <MapPin size={32} className="text-[#d4af37] mx-auto mb-6" />
          <p className="text-xs uppercase tracking-[0.3em] text-[#d4af37] mb-3">
            Zone de livraison
          </p>
          <h2 className="font-display text-4xl sm:text-5xl leading-none mb-8">
            De <span className="italic text-[#d4af37]">Cannes</span> à{" "}
            <span className="italic text-[#d4af37]">Monaco</span>
          </h2>
          <div className="flex flex-wrap justify-center gap-x-1 gap-y-3 text-sm text-gray-300 max-w-3xl mx-auto">
            {[
              "Cannes",
              "Antibes",
              "Juan-les-Pins",
              "Cagnes-sur-Mer",
              "Nice",
              "Villefranche-sur-Mer",
              "Beaulieu-sur-Mer",
              "Saint-Jean-Cap-Ferrat",
              "Èze",
              "Cap-d'Ail",
              "Monaco",
            ].map((z, i, arr) => {
              const city = CITY_LIST.find((c) => c.name === z || c.nameFull === z);
              return (
              <React.Fragment key={z}>
                {city ? <a href={city.path} className="underline underline-offset-4 decoration-[#d4af37]/40 hover:text-[#d4af37] transition-colors" data-testid={`livraison-city-${city.slug}`}>{z}</a> : <span>{z}</span>}
                {i < arr.length - 1 && (
                  <span className="text-[#d4af37]/40 mx-2">•</span>
                )}
              </React.Fragment>
              );
            })}
          </div>
          <p className="text-xs text-gray-500 mt-8 uppercase tracking-[0.2em]">
            Hors zone ? Appelez-nous, on regarde ensemble.
          </p>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 sm:py-32 bg-[#0a0f1a] relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center relative z-10">
          <p className="text-xs uppercase tracking-[0.3em] text-[#d4af37] mb-3">
            Envie de quelque chose ?
          </p>
          <h2 className="font-display text-4xl sm:text-6xl leading-none mb-8">
            Une <span className="italic text-[#d4af37]">envie</span>, un appel,
            <br />
            trente <span className="italic text-[#d4af37]">minutes</span>.
          </h2>
          <p className="text-gray-300 leading-relaxed max-w-xl mx-auto mb-10">
            Notre équipe est disponible en permanence. Commandez par téléphone
            ou WhatsApp — c'est le moyen le plus rapide.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href={`tel:${PHONE_INTL}`}
              onClick={() => trackLeadClick("final_call")}
              className="inline-flex items-center justify-center gap-2 px-10 py-5 bg-[#d4af37] text-[#0a0f1a] font-medium hover:bg-white transition-colors uppercase tracking-[0.2em] text-xs"
              data-testid="final-call-btn"
            >
              <Phone size={14} />
              {PHONE}
            </a>
            <a
              href={WHATSAPP}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackLeadClick("final_whatsapp")}
              className="inline-flex items-center justify-center gap-2 px-10 py-5 border border-[#d4af37] text-[#d4af37] hover:bg-[#d4af37] hover:text-[#0a0f1a] transition-colors uppercase tracking-[0.2em] text-xs"
              data-testid="final-whatsapp-btn"
            >
              <MessageCircle size={14} />
              WhatsApp
            </a>
          </div>
        </div>
      </section>

      <FAQSection />

      {/* MINIMAL FOOTER */}
      <footer className="border-t border-white/5 py-10 bg-[#0a0f1a]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-gray-500">
          <p>
            © {new Date().getFullYear()} Noctua Riviera — Livraison Côte d'Azur
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            <a
              href={`tel:${PHONE_INTL}`}
              onClick={() => trackLeadClick("footer_phone")}
              className="flex items-center gap-2 hover:text-[#d4af37] transition-colors"
              data-testid="footer-phone-link"
            >
              <Phone size={12} /> {PHONE}
            </a>
            <a
              href="mailto:noctua.riviera@gmail.com"
              onClick={() => trackLeadClick("footer_email")}
              className="flex items-center gap-2 hover:text-[#d4af37] transition-colors"
              data-testid="footer-email-link"
            >
              <Mail size={12} /> noctua.riviera@gmail.com
            </a>
            <a
              href="/"
              rel="nofollow noopener"
              className="flex items-center gap-2 text-gray-400 hover:text-[#d4af37] transition-colors underline underline-offset-4 decoration-[#d4af37]/30"
              data-testid="footer-full-menu-link"
            >
              Menu complet
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Livraison;
